import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { LoggerService } from '../services/logger.service';
import { SelectDropdownOptionsApiService } from '../services/select-dropdown-options-api.service';
import { DropdownOption } from './dropdown-option.model';
import { LoadingAndFeedbackState } from './loading-and-feedback-state.type';
import { TableHeaderItem } from './table-header-options.model';
import { WorkPaginatedDocumented } from './work-paginated-documented';
import { downloadBlob } from '../functions/download-blob';
import dayjs from 'dayjs';
import { TranslateService } from '@ngx-translate/core';
import { getFilterFromDropdownOptions } from '../functions/get-filter-from-dropdown-options';

export interface TableBase<T> {
  data?: T[];
  dataPaginated?: (WorkPaginatedDocumented & { data?: Array<T> }) | undefined;
  dataSelectedRows?: T[];
  paginationOptions?: TablePaginationOptions;
  filterDropdownOptions: DropdownOption[];
  tableHeaderItems: TableHeaderItem[];
  loadingState?: LoadingAndFeedbackState;
  load?: () => any;
  sort?: (tableHeaderItem: TableHeaderItem) => any;
  selectDataRow?: (dataRow: T) => any;
  search?: (searchString: string) => any;
  addFilter?: (tableFilter: DropdownOption) => any;
  removeFilter?: (tableFilter: DropdownOption) => any;
}

export class TableBaseClass<T> implements TableBase<T> {
  public data: T[] = [];
  public dataPaginated: WorkPaginatedDocumented<T> | undefined;
  public dataSelectedRows: T[] = [];
  public loadingState: LoadingAndFeedbackState = 'loading';
  public paginationOptions: TablePaginationOptions | undefined;
  public defaultPaginationOptions: Partial<TablePaginationOptions> = {};
  public activeTableHeaderItem: TableHeaderItem | undefined;
  public currentSearchTerm: string | undefined;
  public tableHeaderItems: TableHeaderItem[] = [];
  public filterDropdownOptions: DropdownOption[] = [];
  public filterDropdownOptionsLoadingState: LoadingAndFeedbackState = 'loading';
  public dragAborted = false;
  public draggingActive = false;
  public writeSearchTermToUrl = true; // Steuert, ob der Suchbegriff in die URL geschrieben werden soll
  protected itemList = signal<{ checked: boolean; item: T }[]>([]);
  protected checkedItems = signal<T[]>([]);
  protected selectDropdownOptionsService = inject(SelectDropdownOptionsApiService);
  protected translateService = inject(TranslateService);
  private angularRouter: Router;
  private angularActivatedRoute: ActivatedRoute;
  private destroyRef = inject(DestroyRef);

  constructor() {
    this.angularRouter = inject(Router);
    this.angularActivatedRoute = inject(ActivatedRoute);
    this.angularActivatedRoute.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (params) => {
      const querySearch = params['search'];
      if (querySearch) {
        this.currentSearchTerm = querySearch;
      }
    });
  }

  /**
   * Main method which is responsible for loading paginated data from the API.
   */
  public async load() {
    LoggerService.LOG(
      this,
      'load() method must be implemented individually per component. e.g. load data from API like Events, Items, Users etc. or similar.',
    );
  }

  /**
   * When sorting was changed, sets the current sort table header options and does
   * a load afterwards.
   *
   * @param tableHeaderItem
   */
  public async sort(tableHeaderItem: TableHeaderItem) {
    this.activeTableHeaderItem = tableHeaderItem;
    await this.load();
  }

  /**
   * When pagination was changed by the user (e.g. by selecting a page, or change amount of items per page),
   * this saves these pagination options and does a load with the new settings afterwards.
   *
   * @param tablePaginationOptions
   */
  public async paginationOptionsUpdated(tablePaginationOptions: TablePaginationOptions) {
    this.paginationOptions = tablePaginationOptions;
    await this.load();
  }

  /**
   * Basically just sets the search term which will be sent to the API. Also
   * reset the pagination options, since they'll be overwritten by API after receiving the result.
   *
   * @param searchTerm
   */
  public async search(searchTerm: string) {
    this.currentSearchTerm = searchTerm;
    this.paginationOptions = undefined;
    if (this.writeSearchTermToUrl && this.angularRouter && this.angularActivatedRoute) {
      this.angularRouter.navigate([], {
        relativeTo: this.angularActivatedRoute,
        queryParams: { search: searchTerm },
        queryParamsHandling: 'merge',
        replaceUrl: false, // Pushes it to the history
      });
    }
    await this.load();
  }

  public async handleCdkDrop($event: CdkDragDrop<any[]>): Promise<void> {
    if (!this.dragAborted) {
      if ($event.previousContainer === $event.container) {
        moveItemInArray($event.container.data, $event.previousIndex, $event.currentIndex);
      } else {
        transferArrayItem($event.previousContainer.data, $event.container.data, $event.previousIndex, $event.currentIndex);
      }
      await this.executeLogicOnCdkDrop();
    }
    this.dragAborted = false;
  }

  public onDragEnd() {
    document.removeEventListener('keydown', this.escapeListener);
    this.draggingActive = false;
  }

  protected async downloadCsv() {
    // CSV Header - filter out items that should be hidden in CSV export
    const headers = this.tableHeaderItems;
    const csvRows: string[] = [headers.map((item) => (item.label ? this.translateService.instant(item.label) : item.name)).join(';')];

    // CSV Data Rows
    let csvData: T[] = [];
    if (this.data && this.data.length > 0) {
      csvData = this.data;
    } else if (this.dataPaginated && this.dataPaginated.data && this.dataPaginated.data.length > 0) {
      csvData = this.dataPaginated.data;
    }

    for (const dataItem of csvData) {
      const row = headers.map((headerItem) => {
        // Get the value from the data item using the header name as property path
        const value = this.getNestedPropertyValue(dataItem, headerItem.name);
        return this.formatCsvValue(value);
      });
      csvRows.push(row.join(';'));
    }

    const csvContent = csvRows.join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, `ax-csv-export_${dayjs().format('YYYY-MM-DD')}_${dayjs().unix()}.csv`);
  }

  /**
   * Gets a nested property value from an object using dot notation.
   * E.g., 'user.name' will return obj.user.name
   *
   * @param obj
   * @param path
   * @protected
   */
  protected getNestedPropertyValue(obj: any, path: string): any {
    return path.split('.').reduce((current, prop) => current?.[prop], obj);
  }

  /**
   * Formats a value for CSV export. Can be overridden in child classes for custom formatting.
   *
   * @param value
   * @protected
   */
  protected formatCsvValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'number') {
      // Format numbers with comma as decimal separator for German/Swiss locale
      return value.toString().replace('.', ',');
    }
    if (typeof value === 'boolean') {
      return this.translateService.instant(value ? 'JA' : 'NEIN');
    }
    if (value instanceof Date) {
      return dayjs(value).format('DD.MM.YYYY HH:mm');
    }
    // Escape quotes and wrap in quotes if the value contains special characters
    const stringValue = String(value);
    if (stringValue.includes(';') || stringValue.includes('"') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  }

  /**
   * Returns an array of strings which can be sent to the API.
   * You have to pass the name of the filter to find the correct results.
   *
   * @param filterName
   * @param includeFiltersDespiteSearch
   * @protected
   */
  protected getFilter(filterName: string, includeFiltersDespiteSearch = false): string[] {
    if (this.currentSearchTerm && this.currentSearchTerm !== '' && !includeFiltersDespiteSearch) {
      return [];
    }

    return getFilterFromDropdownOptions(this.filterDropdownOptions, filterName);
  }

  /**
   * Returns the value of the plain filter without suffix or prefix.
   *
   * @param filterName
   * @param includeFiltersDespiteSearch
   * @protected
   */
  protected getPlainFilter(filterName: string, includeFiltersDespiteSearch = false) {
    if (this.currentSearchTerm && this.currentSearchTerm !== '' && !includeFiltersDespiteSearch) {
      return undefined;
    }
    let filterBy = undefined;
    const currentFilterOptions = this.filterDropdownOptions.filter((option) => option.selected === true);
    if (currentFilterOptions && currentFilterOptions.length) {
      const filters = currentFilterOptions.filter((filterOption) => filterOption.name === filterName);
      for (const filter of filters) {
        filterBy = filter.value;
      }
    }
    return filterBy;
  }

  /**
   * Returns the value of the plain filter without suffix or prefix.
   *
   * @param filterName
   * @param includeFiltersDespiteSearch
   * @protected
   */
  protected getPlainFilters(filterName: string, includeFiltersDespiteSearch = false): any[] {
    if (this.currentSearchTerm && this.currentSearchTerm !== '' && !includeFiltersDespiteSearch) {
      return [];
    }
    const filterBy: any[] = [];
    const currentFilterOptions = this.filterDropdownOptions.filter((option) => option.selected === true);
    if (currentFilterOptions && currentFilterOptions.length) {
      const filters = currentFilterOptions.filter((filterOption) => filterOption.name === filterName);
      for (const filter of filters) {
        filterBy.push(filter.value);
      }
    }
    return filterBy;
  }

  /**
   * Returns the amount of items to be displayed per page. Defaults to 25.
   *
   * @protected
   */
  protected getLimit() {
    if (this.paginationOptions) {
      return this.paginationOptions.itemsPerPage;
    } else if (this.defaultPaginationOptions.itemsPerPage) {
      return this.defaultPaginationOptions.itemsPerPage;
    } else {
      return 25;
    }
  }

  /**
   * Returns the current page number. Defaults to 1.
   *
   * @protected
   */
  protected getPage() {
    if (this.paginationOptions) {
      return this.paginationOptions.currentPage;
    } else if (this.defaultPaginationOptions.currentPage) {
      return this.defaultPaginationOptions.currentPage;
    } else {
      return 1;
    }
  }

  /**
   * Returns an array of sort strings which can be sent to the API.
   *
   * @protected
   */
  protected getSortBy() {
    const sortBy: any[] = [];
    if (this.activeTableHeaderItem) {
      sortBy.push(`${this.activeTableHeaderItem.name}:${this.activeTableHeaderItem.sortStatus}`);
    } else {
      // no active sort table header on init, check initial table header items config
      this.tableHeaderItems.forEach((tableHeaderItem) => {
        if (tableHeaderItem.sortStatus) {
          sortBy.push(`${tableHeaderItem.name}:${tableHeaderItem.sortStatus}`);
        }
      });
    }
    return sortBy;
  }

  /**
   * Sets the current pagination options based on the received meta data from the API.
   * Setting some default values as well, if API doesn't deliver them.
   *
   * @protected
   */
  protected setPaginationOptions() {
    if (this.dataPaginated) {
      this.paginationOptions = {
        itemsPerPage: this.dataPaginated.meta.itemsPerPage || 25,
        totalItems: this.dataPaginated.meta.totalItems || 0,
        currentPage: this.dataPaginated.meta.currentPage || 0,
      };
    }
  }

  /**
   * Optional method to be extended in child classes for additional logic after cdk drag & drop.
   */
  protected async executeLogicOnCdkDrop(): Promise<void> {
    // Base class default implementation can be empty
  }

  protected onDragStart() {
    this.draggingActive = true;
    document.addEventListener('keydown', this.escapeListener);
  }

  protected escapeListener = (keyboardEvent: KeyboardEvent) => {
    if (keyboardEvent.key === 'Escape') {
      keyboardEvent.preventDefault();
      this.dragAborted = true;
      // force the drag element to drop
      const dragEndEvent = new DragEvent('mouseup');
      document.dispatchEvent(dragEndEvent);
      document.removeEventListener('keydown', this.escapeListener);
    }
  };

  protected async initializeBusinessunitDropdownOptions(filterName?: string) {
    const options = await lastValueFrom(this.selectDropdownOptionsService.businessunits$);
    for (const option of options) {
      this.filterDropdownOptions.push({
        label: option.label,
        name: filterName ? filterName : 'filter.businessunitId',
        operator: '$eq',
        value: option.value,
        group: 'GESCHAEFTSSTELLE',
      });
    }
  }

  protected async initializeBusinessdivisionDropdownOptions(filterName?: string) {
    const options = await lastValueFrom(this.selectDropdownOptionsService.businessdivisions$);
    for (const option of options) {
      this.filterDropdownOptions.push({
        label: option.label,
        name: filterName ? filterName : 'filter.businessdivisionId',
        operator: '$eq',
        value: option.value,
        group: 'GESCHAEFTSBEREICH',
      });
    }
  }

  protected async initializeContainertypeDropdownOptions(filterName?: string) {
    const options = await lastValueFrom(this.selectDropdownOptionsService.containertypes$);
    for (const option of options) {
      this.filterDropdownOptions.push({
        label: option.label,
        name: filterName ? filterName : 'filter.containertypeId',
        operator: '$eq',
        value: option.value,
        group: 'GESCHAEFTSBEREICH',
      });
    }
  }

  protected async initializeTeamDropdownOptions(filterName?: string) {
    const options = await lastValueFrom(this.selectDropdownOptionsService.teams$);
    for (const option of options) {
      this.filterDropdownOptions.push({
        label: option.label,
        name: filterName ? filterName : 'filter.teamId',
        operator: '$eq',
        value: option.value,
        group: 'TEAMS',
      });
    }
  }

  protected async initializeProjektleiterDropdownOptions(filterName?: string) {
    const options = await lastValueFrom(this.selectDropdownOptionsService.projektleiter$);
    for (const option of options) {
      this.filterDropdownOptions.push({
        label: option.label,
        name: filterName ? filterName : 'filter.tpbUserId',
        operator: '$eq',
        value: option.value,
        group: 'PROJEKTLEITER',
      });
    }
  }

  protected createCheckedList(dataList: T[] | undefined): void {
    const list: { checked: boolean; item: T }[] = [];
    if (dataList && dataList.length) {
      dataList.forEach((item) => {
        list.push({ checked: false, item });
      });
    }
    this.itemList.set(list);
  }

  protected toggleCheckItems(allItemsChecked?: boolean, index?: number): void {
    const list = this.itemList();

    if (allItemsChecked !== undefined) {
      // Toggle all items
      list.forEach((item) => {
        item.checked = allItemsChecked;
      });
    } else if (index !== undefined) {
      // Toggle a specific item
      list[index].checked = !list[index].checked;
    }

    this.itemList.set(list);
    this.updateCheckedList();
  }

  private updateCheckedList(): void {
    const list = this.itemList();
    const checked = list.filter((item) => item.checked).map((item) => item.item);
    this.checkedItems.set(checked);

    const selectableColumn = this.tableHeaderItems.find((item) => item.selectable === true);
    if (selectableColumn) {
      selectableColumn.selectedStatus = checked.length === list.length;
    }
  }
}

export interface TablePaginationOptions {
  itemsPerPage: number;
  totalItems: number;
  currentPage: number;
}
